import { fmtNum } from '../lib/format.ts'
import { noticesFor, type NoticeLike } from './courseStatus.ts'
import type { JobRow, TemplateVersionRow } from './plan.ts'
import type { RosterPerson } from './planUi.ts'
import { outsideEmployment } from './rotation.ts'
import { intervalBounds, msBetween, type WorkIntervalRow, type WorkSessionRow } from './work.ts'

/** Hours planned against hours worked (M16-F, docs/M16.md "F"; benchmark 3, GK-035): the pure rules of the week grid's header line ("besoin 42 h /
 *  dispo 48 h") and of the job page's "prévu 90 min · réalisé 70 min". The server's one read, `api.plan_actuals` (migration 20261201100500), gives per
 *  job what it was planned to take and what it took, and per template version the median of the last twelve finished jobs; this file turns the
 *  mirrored week (`ops.job`, the template versions) and the roster into hours needed against hours available, per day, and sums the device's own
 *  intervals for a job when the server cannot be asked. Advice for the planner, never stored, never per person: nothing here ranks anybody. */

/** The server's window for a template version's median (api.plan_actuals); printed on the screen as the rule. */
export const MEDIAN_OF_LAST = 12
/** Hours one person gives a working day. A default the owner has not seen, printed "(à confirmer)" beside the line; a half day for "partial". */
export const HOURS_PER_PERSON_DAY = 7

export interface ActualsJob {
  job_id: string; local_date: string; template_version_id: string | null; state: string
  /** the version's default duration × the crew the job needs; null without a template or a duration */
  planned_min: number | null
  /** the minutes that count (a timer's, an accepted entry's; a correction replaces what it supersedes), a running one to `as_of` */
  actual_min: number
  /** a manual entry still awaiting review, summed apart */
  actual_pending_min: number
  actual_open: boolean
  crew: number
  outcome: 'complete' | 'partial' | null
}
export interface ActualsMedian { template_version_id: string; median_min: number; n: number }
export interface PlanActuals { from: string; to: string; as_of: string; jobs: ActualsJob[]; medians: ActualsMedian[] }

export interface AvailabilityRow { person_id: string; local_date: string; status: 'partial' | 'off' | 'working' }

/** What a job was planned to take, as the server computes it: the version's default duration × the crew it needs (`required_crew`, else the people on
 *  it, at least one: a job that names no crew size takes one person, the board's rule). Null without a template or a duration. */
export function plannedMinutes(job: Pick<JobRow, 'required_crew'> & { crew: readonly unknown[] }, version: Pick<TemplateVersionRow, 'default_duration_min'> | undefined): number | null {
  if (!version || version.default_duration_min === null || version.default_duration_min === undefined) return null
  return version.default_duration_min * (job.required_crew ?? Math.max(job.crew.length, 1))
}

export interface JobEstimate { min: number | null; basis: 'median' | 'default' | 'none' }
/** The minutes a planned job will take: the median of the last jobs of its template version when the server gave one, else the version's default
 *  × the crew, else unknown. */
export function jobEstimate(job: Pick<JobRow, 'required_crew' | 'template_version_id'> & { crew: readonly unknown[] }, versions: ReadonlyMap<string, Pick<TemplateVersionRow, 'default_duration_min'>>,
                            medians: ReadonlyMap<string, ActualsMedian> | null): JobEstimate {
  const m = job.template_version_id ? medians?.get(job.template_version_id) : undefined
  if (m && m.n > 0) return { min: m.median_min, basis: 'median' }
  const p = plannedMinutes(job, job.template_version_id ? versions.get(job.template_version_id) : undefined)
  return p === null ? { min: null, basis: 'none' } : { min: p, basis: 'default' }
}

export interface DayEstimate {
  day: string
  /** the live jobs of the day (cancelled ones out) */
  jobs: number
  /** the sum of the estimates of the jobs that have one */
  neededMin: number
  /** jobs with no estimate (no template, or no duration): counted, never guessed */
  unknown: number
  /** how many of those jobs were estimated from a median */
  fromMedian: number
  /** the people of the roster whose employment covers the day and who are not away */
  people: number
  /** among them, those "partial" that day (half a day) */
  partial: number
  availableMin: number
  /** needed beyond available: a warning on the line, never a refusal */
  short: boolean
}

/** Hours needed against hours available, one line per day shown. The need: every job of the day that is not cancelled (done ones were planned too),
 *  each at its estimate (`jobEstimate`); jobs with none are counted apart. The availability: the roster's people whose employment covers the day
 *  (`RosterPerson.employedFrom / employedUntil`, M20) and who are not "off" that day (the server's overview, `hr.availability`), a "partial" day
 *  counting half, × `hoursPerDay`. No weather, no ground condition: the sentence on the screen says so. */
export function estimateWeek(days: readonly string[], jobs: ReadonlyArray<Pick<JobRow, 'scheduled_local_date' | 'state' | 'required_crew' | 'template_version_id'> & { crew: readonly unknown[] }>,
                             versions: ReadonlyArray<Pick<TemplateVersionRow, 'id' | 'default_duration_min'>>, medians: ReadonlyArray<ActualsMedian> | null,
                             roster: ReadonlyArray<Pick<RosterPerson, 'id' | 'employedFrom' | 'employedUntil'>>, availability: ReadonlyArray<AvailabilityRow>,
                             opts: { hoursPerDay?: number } = {}): DayEstimate[] {
  const hoursPerDay = opts.hoursPerDay ?? HOURS_PER_PERSON_DAY
  const byVersion = new Map(versions.map((v) => [v.id, v]))
  const byMedian = medians ? new Map(medians.map((m) => [m.template_version_id, m])) : null
  const avail = new Map(availability.map((a) => [`${a.person_id}:${a.local_date}`, a.status]))
  return days.map((day) => {
    const live = jobs.filter((j) => j.scheduled_local_date === day && j.state !== 'cancelled')
    let neededMin = 0, unknown = 0, fromMedian = 0
    for (const j of live) {
      const e = jobEstimate(j, byVersion, byMedian)
      if (e.min === null) unknown++
      else { neededMin += e.min; if (e.basis === 'median') fromMedian++ }
    }
    let people = 0, partial = 0
    for (const p of roster) {
      if (outsideEmployment(day, p.employedFrom, p.employedUntil)) continue
      const st = avail.get(`${p.id}:${day}`)
      if (st === 'off') continue
      people++
      if (st === 'partial') partial++
    }
    const availableMin = Math.round((people - partial / 2) * hoursPerDay * 60)
    return { day, jobs: live.length, neededMin, unknown, fromMedian, people, partial, availableMin, short: neededMin > availableMin }
  })
}

/** "42 h", "42,5 h": minutes as hours to the nearest half hour, in the person's language (the M14 rule: no trailing zero); the unit is bound to its
 *  number by a non-breaking space, so a narrow column wraps "besoin" / "1,5 h", never "1,5" / "h". */
export function hoursText(min: number, lang: string): string {
  const h = Math.round((min / 60) * 2) / 2
  return `${fmtNum(h, lang, 1)} h`
}

export interface DeviceActual { actualMin: number; pendingMin: number; open: boolean; sessions: number }
/** What a job took according to this device's copy of the work records (the mirrored sessions and intervals), by the server's own rule: a correction
 *  replaces what it supersedes, a timer's or an accepted entry counts, a pending manual entry is summed apart, a rejected one for nothing, a running one
 *  to `now`. Shown when the server cannot be asked; the server's answer replaces it online. */
export function deviceActual(jobId: string, sessions: ReadonlyArray<Pick<WorkSessionRow, 'id' | 'job_id'>>, intervals: ReadonlyArray<Pick<WorkIntervalRow, 'work_session_id' | 'during' | 'review_state' | 'superseded_by_id'>>,
                             now: number): DeviceActual {
  const own = new Set(sessions.filter((s) => s.job_id === jobId).map((s) => s.id))
  let counted = 0, pending = 0, open = false
  for (const i of intervals) {
    if (!own.has(i.work_session_id) || i.superseded_by_id || i.review_state === 'rejected') continue
    const b = intervalBounds(i.during)
    const ms = msBetween(b.from, b.to, now)
    if (i.review_state === 'pending') pending += ms
    else { counted += ms; if (b.from && !b.to) open = true }
  }
  return { actualMin: Math.round(counted / 60_000), pendingMin: Math.round(pending / 60_000), open, sessions: own.size }
}

export interface WeekNotice<N extends NoticeLike> { notice: N; days: string[]; all: boolean }
/** The notices in force on the days a week grid shows, each once with the days it covers among them (B1's `noticesFor` per day, so a notice that
 *  spans the week is one line, not seven): in the order of their first day, then the day's own order (the competition first). */
export function weekNotices<N extends NoticeLike>(notices: readonly N[] | undefined, days: readonly string[]): WeekNotice<N>[] {
  const out = new Map<string, WeekNotice<N>>()
  for (const day of days) {
    for (const n of noticesFor(notices, day)) {
      const w = out.get(n.id)
      if (w) w.days.push(day)
      else out.set(n.id, { notice: n, days: [day], all: false })
    }
  }
  return [...out.values()].map((w) => ({ ...w, all: w.days.length === days.length }))
}
